import type { PaletteOptions } from "@mui/material/styles";

/**
 * The Professional palette — the single definition of every colour in the app.
 *
 * Its own module rather than an inline block in `Theme-variable.tsx` because
 * `tokens.ts` reads `tint` and `avatarHues` off it, and importing
 * `Theme-variable.tsx` from `tokens.ts` would be a cycle
 * (`Theme-variable` → `Override` → `tokens`).
 */
const palette = {
  mode: "light",
  /**
   * Professional's teal. `main` for buttons, links, active text and progress
   * fill; `dark` for FABs, filled step dots and hover; `darker` for banners
   * and the filled track bar.
   */
  primary: {
    main: "#007E8C",
    light: "#3AA3AE",
    dark: "#005E69",
    darker: "#082F37",
    contrastText: "#ffffff",
  },
  /**
   * Professional collapses `secondary` onto the deep band `#082F37` — the
   * same value `primary.darker` carries. Kept as its own slot because
   * `secondary` has ~95 call sites and `primary.darker` is a ramp stop; the
   * two mean different things even at the same hex.
   */
  secondary: {
    main: "#082F37",
    light: "#3E5354",
    dark: "#000507",
    contrastText: "#ffffff",
  },
  /**
   * The dark surface ramp: `dark` is the sidebar and app header band,
   * `darkest` the active sidebar row, `medium` a lighter stop. Unchanged from
   * Playful — Professional specifies the same shell tones.
   */
  neutral: {
    medium: "#3E5354",
    dark: "#222F30",
    darkest: "#16201F",
  },
  /**
   * Growth actions ONLY — invite, share, celebrate, section eyebrows. Deeper
   * and browner than Playful's `#E0642A`. If it is not a growth action, it is
   * not accent.
   */
  accent: {
    main: "#AE4C1C",
    light: "#C86A3B",
    dark: "#8E3D15",
    contrastText: "#FFFFFF",
  },
  /** Logo and brand marks. Also the informational blue's source. */
  brand: {
    logo: "#2274A5",
  },
  success: {
    main: "#3E8F77",
    light: "#6FB39F",
    dark: "#2F7D67",
    contrastText: "#ffffff",
  },
  info: {
    main: "#2274A5",
    light: "#5B9BC6",
    dark: "#1A5C84",
    contrastText: "#ffffff",
  },
  /**
   * `main` is the lighter alert red, for banners and "Past Due" text. `dark`
   * is the destructive-action red a contained `color="error"` button takes —
   * see `Override.tsx`'s `MuiButton` `variants` (contained-error style).
   */
  error: {
    main: "#E57373",
    light: "#F09B9B",
    dark: "#A83226",
    contrastText: "#ffffff",
  },
  warning: {
    main: "#D98E12",
    light: "#E8AE50",
    dark: "#A96C08",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#16211F",
    secondary: "#5C6667",
    disabled: "#87908F",
  },
  /**
   * The neutral ramp, mapped onto MUI's numeric `grey` slots. Each slot keeps
   * the ROLE its comment describes and takes Professional's tone for that
   * role, so the ~30 `grey[…]` call sites outside `src/theme/` keep meaning
   * what they meant.
   *
   * Two slots land on the same `#F4F3F0`: the ramp has one inset-surface tone
   * and this ladder had two near-identical light greys for it.
   */
  grey: {
    /** Card hairline, and the border/backing tint for avatars and logos. */
    50: "#DEDEDE",
    /** Table header, input prefix box. */
    100: "#F4F4F4",
    /** Selected-state background. Ramp: inset panel. */
    200: "#F4F3F0",
    /** Disabled-state background. Ramp: disabled field fill. */
    300: "#E7E5E0",
    /** Input border, scrollbar thumb, disabled line, empty step dots. */
    400: "#B0ADA4",
    /** Disabled text, meta, captions on white. */
    500: "#87908F",
    /** Icon glyphs, table cells, secondary rows. */
    600: "#4A5556",
    /** Inset surface — the date-range chips. Same ramp tone as `200`. */
    700: "#F4F3F0",
    /** Long-form body copy inside cards. */
    800: "#2A3237",
  },
  background: {
    /**
     * Professional's page ground: a neutral soft grey, not Playful's warm
     * greige. Cards stay white — the `MuiPaper` override pins `common.white` —
     * so every Paper-backed surface lifts off the ground.
     */
    default: "#ECECEC",
    paper: "#FFFFFF",
  },
  /** The hairline on every surface. */
  divider: "#DEDEDE",
  /**
   * Status tint pairs — one background/foreground pair per status, shared by
   * chips, alert rules, stance tags and confidence badges so a status reads
   * identically wherever it appears.
   *
   * This is the single definition. `tokens.designSystem.statusTints` derives
   * from it (see `tokens.ts`) rather than restating the values, so there is
   * one place to edit.
   */
  tint: {
    /** MUI `success`. */
    published: { bg: "#E4F4F0", fg: "#2F7D67" },
    /** MUI `error`. */
    pastDue: { bg: "#FDEAEA", fg: "#A83226" },
    /** MUI `warning`. */
    draft: { bg: "#FFF3E0", fg: "#8A5A00" },
    /** MUI `default`. */
    archived: { bg: "#ECEFF0", fg: "#4A5556" },
    /** MUI `primary`. Also the selected menu row and light in-page nav row. */
    selected: { bg: "#E2F2F4", fg: "#00707D" },
    /** MUI `accent`. The count pill beside a section header. */
    count: { bg: "#EBDCD1", fg: "#AE4C1C" },
    /** MUI `info`. */
    info: { bg: "#EAF1FA", fg: "#134A86" },
  },
  /**
   * Avatar and stripe hues. Cycle in order by index so two adjacent members
   * never land on the same colour — the order is the specification, not an
   * arbitrary list, so consumers index into it rather than pick.
   */
  avatarHues: [
    "#007E8C",
    "#AE4C1C",
    "#3E8F77",
    "#7D5FC7",
    "#A83226",
    "#D98E12",
    "#5C8F95",
  ],
  common: {
    white: "#FFFFFF",
    black: "#000000",
  },
  action: {
    selected: "#F4F3F0",
    disabledBackground: "#E7E5E0",
    disabled: "#87908F",
    hoverOpacity: 0.02,
    hover: "rgba(0, 0, 0, 0.03)",
  },
} satisfies PaletteOptions;

export default palette;
